export interface ScriptSceneSnapshot {
    index: number;
    startSec: number;
    endSec: number;
    timelineStartSec: number;
    timelineEndSec: number;
}

const EPS = 1e-3;

type TimelineWindow = {
    startSec: number;
    endSec: number;
};

const getClipOutSec = (scriptClip: any): number => {
    const inSec = Number(scriptClip?.range?.[0]?.seconds ?? 0);
    const outFromRange = Number(scriptClip?.range?.[1]?.seconds ?? NaN);
    const outFromDuration = Number(scriptClip?.duration?.seconds ?? NaN);

    if (Number.isFinite(outFromRange) && outFromRange > inSec + EPS) return outFromRange;
    if (Number.isFinite(outFromDuration) && outFromDuration > inSec + EPS) return outFromDuration;
    return inSec;
};

const getSourceTimelineWindows = (scriptClip: any): TimelineWindow[] => {
    const windows: TimelineWindow[] = [];
    const inputConfigs: any[] =
        typeof scriptClip?.getInputConfigs === "function"
            ? (scriptClip.getInputConfigs() as any[])
            : [];

    const clipOutSec = getClipOutSec(scriptClip);

    for (const cfg of inputConfigs) {
        const cfgPlaybackRate = Number(cfg?.playbackRate ?? 1.0) || 1.0;
        const segments: any[] = Array.isArray(cfg?.segments) ? cfg.segments : [];

        if (segments.length > 0) {
            for (const seg of segments) {
                const offsetSec = Number(seg?.offsetSec ?? NaN);
                const segPlaybackRate = Number(seg?.playbackRate ?? cfgPlaybackRate) || 1.0;
                const inSec = Number(seg?.range?.inSec ?? NaN);
                const outSec = Number(seg?.range?.outSec ?? NaN);
                if (!Number.isFinite(offsetSec) || !Number.isFinite(segPlaybackRate) || segPlaybackRate <= 0) continue;
                if (!Number.isFinite(inSec) || !Number.isFinite(outSec) || outSec <= inSec + EPS) continue;

                const dur = (outSec - inSec) / segPlaybackRate;
                const endSec = offsetSec + dur;
                if (endSec > offsetSec + EPS) {
                    windows.push({ startSec: offsetSec, endSec });
                }
            }
            continue;
        }

        const offsetSec = Number(cfg?.offsetSec ?? 0);
        const playbackRate = Number(cfg?.playbackRate ?? 1.0) || 1.0;
        const inSec = Number(cfg?.range?.inSec ?? 0);
        const outSecRaw = Number(cfg?.range?.outSec ?? NaN);
        if (!Number.isFinite(offsetSec) || !Number.isFinite(playbackRate) || playbackRate <= 0) continue;
        if (!Number.isFinite(inSec)) continue;

        const hasFiniteRangeOut = Number.isFinite(outSecRaw) && outSecRaw > inSec + EPS;
        const dur = hasFiniteRangeOut
            ? (outSecRaw - inSec) / playbackRate
            : Math.max(0, clipOutSec - offsetSec);
        const endSec = offsetSec + dur;
        if (endSec > offsetSec + EPS) {
            windows.push({ startSec: offsetSec, endSec });
        }
    }

    return windows;
};

export const getScriptScenes = (scriptClip: any): ScriptSceneSnapshot[] => {
    if (!scriptClip || typeof scriptClip.getScenes !== "function") return [];
    const scenesRaw: any[] = Array.isArray(scriptClip.getScenes()) ? scriptClip.getScenes() : [];
    return scenesRaw
        .map((scene: any) => ({
            index: Number(scene?.index ?? NaN),
            startSec: Number(scene?.startSec ?? NaN),
            endSec: Number(scene?.endSec ?? NaN),
            timelineStartSec: Number(scene?.timelineStartSec ?? NaN),
            timelineEndSec: Number(scene?.timelineEndSec ?? NaN),
        }))
        .filter((scene) => {
            return (
                Number.isFinite(scene.index) &&
                Number.isFinite(scene.startSec) &&
                Number.isFinite(scene.endSec) &&
                scene.endSec > scene.startSec + EPS
            );
        })
        .map((scene) => ({
            ...scene,
            timelineStartSec: Number.isFinite(scene.timelineStartSec) ? scene.timelineStartSec : 0,
            timelineEndSec: Number.isFinite(scene.timelineEndSec) ? scene.timelineEndSec : 0,
        }));
};

export const isScriptSceneEmpty = (scriptClip: any, scene: Pick<ScriptSceneSnapshot, "startSec" | "endSec"> | null | undefined): boolean => {
    if (!scriptClip || !scene) return false;
    const startSec = Number(scene.startSec ?? NaN);
    const endSec = Number(scene.endSec ?? NaN);
    if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec + EPS) return false;

    const sourceWindows = getSourceTimelineWindows(scriptClip);
    if (sourceWindows.length > 0) {
        const hasSourceOverlap = sourceWindows.some((window) => {
            const winStart = Number(window?.startSec ?? NaN);
            const winEnd = Number(window?.endSec ?? NaN);
            if (!Number.isFinite(winStart) || !Number.isFinite(winEnd) || winEnd <= winStart + EPS) return false;
            return winStart < endSec - EPS && startSec < winEnd - EPS;
        });
        return !hasSourceOverlap;
    }

    const segments: any[] = Array.isArray(scriptClip?.scriptSegments) ? scriptClip.scriptSegments : [];
    if (segments.length === 0) return true;

    const hasContentOverlap = segments.some((segment: any) => {
        const srcStartSec = Number(segment?.srcStartSec ?? NaN);
        const srcEndSec = Number(segment?.srcEndSec ?? NaN);
        if (!Number.isFinite(srcStartSec) || !Number.isFinite(srcEndSec) || srcEndSec <= srcStartSec + EPS) return false;
        return srcStartSec < endSec - EPS && startSec < srcEndSec - EPS;
    });
    return !hasContentOverlap;
};

export const findScriptSceneAtTimelineTime = (
    scriptClip: any,
    timelineTimeSec: number,
    toleranceSec: number = 1e-3,
): ScriptSceneSnapshot | null => {
    const scenes = getScriptScenes(scriptClip);
    if (scenes.length === 0) return null;
    const t = Number(timelineTimeSec ?? NaN);
    if (!Number.isFinite(t)) return null;

    const scene =
        scenes.find(
            (candidate) =>
                Number.isFinite(candidate.timelineStartSec) &&
                Number.isFinite(candidate.timelineEndSec) &&
                t >= candidate.timelineStartSec - toleranceSec &&
                t < candidate.timelineEndSec - toleranceSec,
        ) ??
        scenes.find(
            (candidate) =>
                Number.isFinite(candidate.timelineEndSec) &&
                Math.abs(t - candidate.timelineEndSec) <= Math.max(toleranceSec, 0.02),
        ) ??
        null;

    return scene;
};

export const findScriptSceneByTimelineRange = (
    scriptClip: any,
    timelineStartSec: number,
    timelineEndSec: number,
    toleranceSec: number = 0.02,
): ScriptSceneSnapshot | null => {
    const scenes = getScriptScenes(scriptClip);
    if (scenes.length === 0) return null;
    const start = Number(timelineStartSec ?? NaN);
    const end = Number(timelineEndSec ?? NaN);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

    return (
        scenes.find((scene) => {
            if (!Number.isFinite(scene.timelineStartSec) || !Number.isFinite(scene.timelineEndSec)) return false;
            return (
                Math.abs(scene.timelineStartSec - start) <= toleranceSec &&
                Math.abs(scene.timelineEndSec - end) <= toleranceSec
            );
        }) ?? null
    );
};
